import { useAuth } from '@/hooks'
import { ParticipantController, RegistrantController } from '@/controllers'
import { AuthorizationResponse, Registrant } from '@/interfaces/registrant'
import { Participant } from '@/interfaces/participant'
import { SeasonOrganization } from '@/interfaces/seasonOrganization'
import { mutate } from 'swr'
import { SEASON_ORGANIZATION_KEY } from '@/hooks/useSeasonOrganization'
import { PARTICIPANTS_KEY } from '@/hooks/useParticipants'

export const useRegistrantAuth = () => {
  const { authorize, logout } = useAuth()

  const authorizeRegistrant = async (
    uuid: string,
  ): Promise<{ authData: AuthorizationResponse | null; participants: Participant[] | null }> => {
    const {
      result: [authorizationError, authResponse],
      token,
    } = await RegistrantController.auth(uuid)

    if (authorizationError) {
      throw authorizationError
    }

    authorize(token!, authResponse.registrant?.id ?? null)

    const [seasonOrgResponse, participantsResponse] = await Promise.all([
      RegistrantController.currentSeasonOrganization(),
      ParticipantController.list(),
    ])

    const error = seasonOrgResponse.result[0] || participantsResponse.result[0]

    if (error) {
      throw error
    }

    // Update SWR cache for useSeasonOrganization with the fresh data
    // This ensures the hook returns correct data after authorization
    await mutate(SEASON_ORGANIZATION_KEY, seasonOrgResponse, { revalidate: false })

    // Update SWR cache for useParticipants with the fresh data
    // This ensures the hook returns correct data after authorization
    await mutate(PARTICIPANTS_KEY, participantsResponse, { revalidate: false })

    return {
      authData: authResponse,
      participants: participantsResponse.result[1],
    }
  }

  const signInRegistrant = async (
    token: string,
  ): Promise<{ registrant: Registrant | null; seasonOrganization: SeasonOrganization | null }> => {
    authorize(token, null)

    const {
      result: [error, registrant],
      code,
    } = await RegistrantController.me()

    if (error) {
      if (code > 400 && code < 500) {
        logout()
      }

      throw error
    }

    authorize(token, registrant.id)

    const seasonOrgResponse = await RegistrantController.currentSeasonOrganization()

    // Update SWR cache for useSeasonOrganization with the fresh data
    await mutate(SEASON_ORGANIZATION_KEY, seasonOrgResponse, { revalidate: false })

    return {
      registrant,
      seasonOrganization: seasonOrgResponse.result[1],
    }
  }

  return {
    authorizeRegistrant,
    signInRegistrant,
  }
}
